import { useState, useCallback } from 'react';

/**
 * Custom hook for standardized popup management.
 * Replaces jQuery-based popup toggle logic with React state.
 * 
 * @param {boolean} initialState - Initial open state (default: false)
 * @returns {{ isOpen: boolean, open: () => void, close: () => void, toggle: () => void }}
 */
export function usePopup(initialState = false) {
    const [isOpen, setIsOpen] = useState(initialState);
    
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen(prev => !prev), []);
    
    return { isOpen, open, close, toggle };
}

/**
 * Custom hook for managing multiple popups.
 * Useful for pages with many popup components.
 * 
 * @param {string[]} popupNames - Array of popup names to manage
 * @returns {{ popups: Object, openPopup: (name) => void, closePopup: (name) => void, togglePopup: (name) => void }}
 */
export function useMultiplePopups(popupNames = []) {
    const [popups, setPopups] = useState(() => 
        popupNames.reduce((acc, name) => ({ ...acc, [name]: false }), {})
    );
    
    const openPopup = useCallback((name) => {
        setPopups(prev => ({ ...prev, [name]: true }));
    }, []);
    
    const closePopup = useCallback((name) => {
        setPopups(prev => ({ ...prev, [name]: false }));
    }, []);
    
    const togglePopup = useCallback((name) => {
        setPopups(prev => ({ ...prev, [name]: !prev[name] }));
    }, []);
    
    const closeAllPopups = useCallback(() => {
        setPopups(prev => 
            Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {})
        );
    }, []);
    
    return { popups, openPopup, closePopup, togglePopup, closeAllPopups };
}

export default usePopup;

